import { CSSProperties } from 'react';
import { ElementHighlighterConfig, DEFAULT_CONFIG } from './types';
import { UID } from '@fullstory/util/uid';

/**
 * This is the logic encapsulating the rectangle that highlight an element
 * in the dom. We highlight an element by adding another element to the dom
 * that is transparent with a border at the exact location and size of the
 * to be highlighted element
 */
export class HighlighterRect {
  private _rectElement: HTMLElement;
  private _config: ElementHighlighterConfig;

  constructor(elemToHighlight: Element, config: ElementHighlighterConfig = DEFAULT_CONFIG) {
    this._config = config;
    this._rectElement = this._createHighlighterElement(elemToHighlight);
  }
  /**
   * create necessary inline style object for the highlighter rect
   */
  private _getInlineStyles(elemToHighlight: Element): CSSProperties {
    const elemRect = elemToHighlight.getBoundingClientRect();
    const positionStyles: CSSProperties = {
      width: `${elemRect.width}px`,
      height: `${elemRect.height}px`,
      top: `${elemRect.top}px`,
      left: `${elemRect.left}px`,
    };
    return {
      boxSizing: 'border-box',
      WebkitBoxSizing: 'border-box',
      borderRadius: 3,
      boxShadow: `inset 0 0 4px 0 ${this._config.boxShadowColor}`,
      WebkitTransition: 'all 0.1s ease-in-out',
      transition: 'all 0.1s ease-in-out',
      border: `2px solid ${this._config.borderColor}`,
      position: 'fixed',
      display: 'inline-block',
      zIndex: 2147483647,
      pointerEvents: 'none',
      padding: 0,
      margin: 0,
      backgroundColor: 'transparent',
      ...positionStyles,
    };
  }
  /**
   * creates and add highlighter element to the DOM
   */
  private _createHighlighterElement(elemToHighlight: Element): HTMLDivElement {
    try {
      // element can be highlighted from multiple doms or iframes. We always
      // need to ensure we are creating and attaching the highlighter rect to
      // the correct dom
      const newHighlighter = elemToHighlight.ownerDocument.createElement('div');
      const styles = this._getInlineStyles(elemToHighlight);
      Object.keys(styles).forEach(style => {
        newHighlighter.style[style] = styles[style];
      });
      newHighlighter.id = UID();
      newHighlighter.classList.add(this._config.className);
      elemToHighlight.ownerDocument.body.appendChild(newHighlighter);
      return newHighlighter;
    } catch (e) {
      console.error('Error: unable to create highlighted element', e);
      return undefined;
    }
  }

  position(elemToHighlight: Element) {
    // sanity check
    if (!this._rectElement) {
      return;
    }

    const styles = this._getInlineStyles(elemToHighlight);
    Object.keys(styles).forEach(style => {
      this._rectElement.style[style] = styles[style];
    });
  }

  dispose() {
    // sanity check
    if (!this._rectElement) {
      return;
    }

    try {
      this._rectElement.remove();
      this._rectElement = undefined;
    } catch (e) {
      console.error('Error: unable to dispose of highlighted elements', e);
    }
  }
}
